import { formatAssetUrl } from '../../utils'

export const retinaScaleFactor = 3

const resizeAsset = (url) =>
  formatAssetUrl(url, { width: 20 * retinaScaleFactor })

const avatarFor = ({ member }) => {
  let imageUrl

  // an already decorated user
  if (member.imageUrl) {
    imageUrl = member.imageUrl
  }
  // member is actually a user
  if (member.avatar) {
    imageUrl = member.avatar.url
  }
  // member is not yet decorated
  if (member.user && member.user.avatar) {
    imageUrl = member.user.avatar.url
  }

  if (imageUrl) {
    return resizeAsset(imageUrl)
  }
  return undefined
}

const titleFor = ({ member }) => {
  let title = member.title
  // member is actually a user
  if (member.first_name) {
    title = `${member.first_name} ${member.last_name}`
  }
  // member is not yet decorated
  if (member.user) {
    title = `${member.user.first_name} ${member.user.last_name}`
  }
  return title
}

/*
 * takes a member object and decorates it for use within
 * a multiselect component.
 *
 * @param {Object}      member
 */
export const createUserFromMember = (member) => ({
  title: titleFor({ member }),
  imageUrl: avatarFor({ member }),
  id: member.id || parseInt(member.user.id, 10),
  isRequired: member.isRequired,
  removable: !member.isRequired,
  disabled: member.isRequired,
})

/*
 * Loops through a list of users and members to determine
 * which ones to should be added to a privacy group as
 * non-required users. We do this for users that have
 * in some way interacted with the Story/Moment/Content
 * in case they need to be a part of the privacy group
 * and not be left out by accident.
 *
 * @param {Array}       accessGroupMembers
 *      users considered "collaborators" on the entity
 * @param {Number}      entityId
 * @param {String}      entityType
 * @param {Array}       users
 *      all users from Redux
 * @param {Array}       value
 *      current value of the Private People Picker
 */
export const getSuggestedUsers = ({
  accessGroupMembers,
  entityId,
  entityType,
  value,
}) => {
  let members = []
  if (accessGroupMembers) {
    members = (accessGroupMembers[entityType] || {})[entityId] || []
  }

  // find suggested users not in initial value that should be added
  const suggestedUsers = members.reduce((accum, current) => {
    const userIndex = value.findIndex(
      (user) => parseInt(user.id, 10) === parseInt(current.id, 10)
    )
    const isUserAlreadyAdded = userIndex > -1
    if (!isUserAlreadyAdded) {
      const user = createUserFromMember(current)
      accum.push(user)
    }
    return accum
  }, [])

  return suggestedUsers
}

/*
 * Takes the initial value of users to display, and checks
 * if they are members first (collaborators). If they are
 * we use the member (which has the is_required flag)
 * intead of the user.
 *
 * @param {Array}       accessGroupMembers
 *      users considered "collaborators" on the entity
 * @param {Number}      entityId
 * @param {String}      entityType
 *      callback for when we have made the call to load
 *      collaborators
 * @param {Array}       value
 *      current value of the Private People Picker
 */
export const getDisplayValue = ({
  accessGroupMembers,
  entityId,
  entityType,
  value,
}) => {
  let members = []
  if (accessGroupMembers) {
    members = (accessGroupMembers[entityType] || {})[entityId] || []
  }

  // prefer members if they exist as initial value and members
  const initialUsers = value.reduce((accum, current) => {
    const foundUser = members.find(
      (member) => parseInt(member.id, 10) === parseInt(current.id, 10)
    )
    const userToAdd = foundUser ? createUserFromMember(foundUser) : current
    return accum.concat(userToAdd)
  }, [])

  return initialUsers
}
